import React, { Suspense, lazy, useContext } from "react";
import { CreatorProvider, CreatorContext } from "./context/CreatorContext";
import { TokenProvider, TokenContext } from "./context/TokenContext";
import { WebTemplateProvider } from "./context/WebTemplateContext";
import { PlatformProvider } from "./context/PlatformContext";
import { UserProvider } from "./context/UserContext";
import Main from "./components/Main";
import {
	BrowserRouter,
	Routes,
	Route,
	useLocation,
	Navigate,
} from "react-router-dom";
import { ShopCartProvider } from "./shop/ShopCartContext";
import { CookieConsentProvider } from "./context/CookieConsentContext";
import CookieBanner from "./components/CookieBanner";
import { isPathIdPrefixHost } from "./utils/appPath";
import { useGoogleAnalytics } from "./hooks/useGoogleAnalytics";

const DigitalMenuView = lazy(() => import("./components/DigitalMenuView"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const ProductDetail = lazy(() => import("./shop/ProductDetail"));
const ShopCart = lazy(() => import("./shop/ShopCart"));
const ShopCheckout = lazy(() => import("./shop/ShopCheckout"));
const OrderSuccess = lazy(() => import("./shop/OrderSuccess"));
const CampaignPage = lazy(() => import("./pages/CampaignPage"));
const OrderLookupPage = lazy(() => import("./pages/OrderLookupPage"));

function LoadingSpinner() {
	return (
		<div className="flex flex-col items-center justify-center min-h-screen">
			<div
				className="w-10 h-10 border-4 border-gray-300 rounded-full animate-spin"
				style={{ borderTopColor: "#43BD98" }}
			></div>
		</div>
	);
}

function AppContent() {
	const { isMenuRoute, appId, loading } = useContext(TokenContext);
	const { creator } = useContext(CreatorContext);
	const location = useLocation();
	useGoogleAnalytics();

	if (loading) {
		return <LoadingSpinner />;
	}

	if (isMenuRoute && appId) {
		return (
			<Suspense fallback={<LoadingSpinner />}>
				<DigitalMenuView />
			</Suspense>
		);
	}

	// Rutas con pathId solo en web.applaby.com o localhost; en dominio custom sin pathId
	const usePathIdRoutes = isPathIdPrefixHost();
	const pathSegments = location.pathname.split("/").filter(Boolean);
	const isShopRoute = usePathIdRoutes
		? pathSegments.length >= 2 && pathSegments[1] === "shop"
		: pathSegments.length >= 1 && pathSegments[0] === "shop";

	// Detectar si es restaurant1-18 (excepto restaurant8) con useShopCartFlow (para mostrar modal en lugar de página)
	const appType = creator?.app?.appType?.toLowerCase();
	const appSubtype = creator?.app?.appSubtype?.toLowerCase();
	const isEcommerce = appType === "ecommerce" || appType === "woocommerce";
	const isEcommerceSubtype = appSubtype === "ecommerce";
	const useShopCartFlow = isEcommerce && !isEcommerceSubtype;
	const isRestaurant1 = creator?.webTemplate === "restaurant1";
	const isRestaurant2 = creator?.webTemplate === "restaurant2";
	const isRestaurant3 = creator?.webTemplate === "restaurant3";
	const isRestaurant4 = creator?.webTemplate === "restaurant4";
	const isRestaurant5 = creator?.webTemplate === "restaurant5";
	const isRestaurant6 = creator?.webTemplate === "restaurant6";
	const isRestaurant7 = creator?.webTemplate === "restaurant7";
	const isRestaurant9 = creator?.webTemplate === "restaurant9";
	const isRestaurant10 = creator?.webTemplate === "restaurant10";
	const isRestaurant11 = creator?.webTemplate === "restaurant11";
	const isRestaurant12 = creator?.webTemplate === "restaurant12";
	const isRestaurant13 = creator?.webTemplate === "restaurant13";
	const isRestaurant14 = creator?.webTemplate === "restaurant14";
	const isRestaurant15 = creator?.webTemplate === "restaurant15";
	const isRestaurant16 = creator?.webTemplate === "restaurant16";
	const isRestaurant17 = creator?.webTemplate === "restaurant17";
	const isRestaurant18 = creator?.webTemplate === "restaurant18";
	const shouldUseModal =
		useShopCartFlow &&
		(isRestaurant1 ||
			isRestaurant2 ||
			isRestaurant3 ||
			isRestaurant4 ||
			isRestaurant5 ||
			isRestaurant6 ||
			isRestaurant7 ||
			isRestaurant9 ||
			isRestaurant10 ||
			isRestaurant11 ||
			isRestaurant12 ||
			isRestaurant13 ||
			isRestaurant14 ||
			isRestaurant15 ||
			isRestaurant16 ||
			isRestaurant17 ||
			isRestaurant18);

	if (isShopRoute) {
		if (shouldUseModal && location.pathname.includes("/shop/product/")) {
			return <Main />;
		}

		const redirectTo = usePathIdRoutes ? `/${pathSegments[0]}` : "/";
		return (
			<Suspense fallback={<LoadingSpinner />}>
				<Routes>
					{usePathIdRoutes ? (
						<>
							<Route
								path="/:pathId/shop"
								element={<Navigate to={redirectTo} replace />}
							/>
							<Route
								path="/:pathId/shop/product/:productId"
								element={<ProductDetail />}
							/>
							<Route path="/:pathId/shop/cart" element={<ShopCart />} />
							<Route path="/:pathId/shop/checkout" element={<ShopCheckout />} />
							<Route path="/:pathId/shop/success" element={<OrderSuccess />} />
						</>
					) : (
						<>
							<Route path="/shop" element={<Navigate to="/" replace />} />
							<Route
								path="/shop/product/:productId"
								element={<ProductDetail />}
							/>
							<Route path="/shop/cart" element={<ShopCart />} />
							<Route path="/shop/checkout" element={<ShopCheckout />} />
							<Route path="/shop/success" element={<OrderSuccess />} />
						</>
					)}
				</Routes>
			</Suspense>
		);
	}

	const isAccountRoute = usePathIdRoutes
		? pathSegments.length >= 2 && pathSegments[1] === "account"
		: pathSegments[0] === "account";
	const isLoginRoute = usePathIdRoutes
		? pathSegments.length >= 2 && pathSegments[1] === "login"
		: pathSegments[0] === "login";

	if (isAccountRoute) {
		if (creator?.webTemplate === "gaps") {
			const basePath = usePathIdRoutes ? `/${pathSegments[0]}/` : "/";
			window.location.replace(`${basePath}#migaps`);
			return null;
		}
		return (
			<Suspense fallback={<LoadingSpinner />}>
				<ProfilePage />
			</Suspense>
		);
	}

	if (isLoginRoute) {
		return (
			<>
				<Main />
				<Suspense fallback={null}>
					<LoginPage />
				</Suspense>
			</>
		);
	}

	const isOrderLookupRoute = usePathIdRoutes
		? pathSegments.length >= 2 && pathSegments[1] === "order-lookup"
		: pathSegments[0] === "order-lookup";

	if (isOrderLookupRoute) {
		return (
			<Suspense fallback={<LoadingSpinner />}>
				<OrderLookupPage />
			</Suspense>
		);
	}

	const isPromoRoute = usePathIdRoutes
		? pathSegments.length >= 2 && pathSegments[1] === "promo"
		: pathSegments.length >= 1 && pathSegments[0] === "promo";

	if (isPromoRoute) {
		return (
			<Suspense fallback={<LoadingSpinner />}>
				<Routes>
					{usePathIdRoutes ? (
						<Route path="/:pathId/promo/:slug" element={<CampaignPage />} />
					) : (
						<Route path="/promo/:slug" element={<CampaignPage />} />
					)}
				</Routes>
			</Suspense>
		);
	}

	return <Main />;
}

export default function App() {
	return (
		<BrowserRouter>
			<TokenProvider>
				<PlatformProvider>
					<CreatorProvider>
						<WebTemplateProvider>
							<CookieConsentProvider>
								<UserProvider>
									<ShopCartProvider>
										<AppContent />
										<CookieBanner />
									</ShopCartProvider>
								</UserProvider>
							</CookieConsentProvider>
						</WebTemplateProvider>
					</CreatorProvider>
				</PlatformProvider>
			</TokenProvider>
		</BrowserRouter>
	);
}
